import DeactivationModalCSEvents from '../../../model/ClickstreamEvent/DeactivationModalCSEvents';

import LeaseClipboard from '../../../images/LeaseClipboard.svg';
import LeaseToSign from '../../../images/LeaseToSign.svg';
import LeaseToUpload from '../../../images/LeaseToUpload.svg';

const STEPS = {
  DEACTIVATION_REASON: 'deactivationReason',
  NOTIFY_LEADS: 'notifyLeads',
  CONGRATULATIONS: 'congratulations',
  LEASES_SURVEY: 'leasesSurvey',
  UPLOAD_SIGNED_LEASE: 'uploadSignedLease',
  LEASES_SURVEY_NEEDS_LEASE: 'needsLeaseSurvey',
  CREATE_CUSTOM_LEASE: 'createCustomLease',
  UPLOAD_UNSIGNED_LEASE: 'uploadUnsignedLease',
  THANK_YOU: 'thankYou',
  CREATE_LEASE_ERROR: 'createLeaseError',
};

const LEASE_CREATION_CTA_STEPS = [STEPS.UPLOAD_SIGNED_LEASE, STEPS.CREATE_CUSTOM_LEASE, STEPS.UPLOAD_UNSIGNED_LEASE];

const LEASES_SURVEY_STEPS = [STEPS.LEASES_SURVEY, STEPS.LEASES_SURVEY_NEEDS_LEASE];

const submitButtonText = {
  [STEPS.DEACTIVATION_REASON]: 'Deactivate listing',
  [STEPS.NOTIFY_LEADS]: 'Send and Next',
  [STEPS.LEASES_SURVEY]: 'Next',
  [STEPS.UPLOAD_SIGNED_LEASE]: 'Upload lease',
  [STEPS.LEASES_SURVEY_NEEDS_LEASE]: 'Next',
  [STEPS.CREATE_CUSTOM_LEASE]: 'Create lease',
  [STEPS.UPLOAD_UNSIGNED_LEASE]: 'Upload lease',
  [STEPS.THANK_YOU]: 'Done',
};

const leaseTypes = {
  ZILLOW: 'zillow',
  SELF_UPLOAD: 'self_upload',
  SIGNED_SELF_UPLOAD: 'signed_self_upload',
};

const leasePathSuffixes = {
  SELF_SERVE: 'selfServe',
  WIZARD: 'wizard',
};

const CREATE_LEASE_CONTENT = {
  [STEPS.UPLOAD_SIGNED_LEASE]: {
    HEADING: 'Upload your signed lease',
    BODY: 'Protect yourself by storing a digital backup of your lease agreement with Zillow. We’ll also send you a reminder before your lease term ends so you can renew or update it.',
    DB_VALUE: 'uploadSignedLease',
    LEASE_TYPE: leaseTypes.SIGNED_SELF_UPLOAD,
    LEASE_PATH_SUFFIX: leasePathSuffixes.SELF_SERVE,
    CONFIRM_CS_EVENT: DeactivationModalCSEvents.ConfirmUploadSignedLease,
    DECLINE_CS_EVENT: DeactivationModalCSEvents.DeclineUploadSignedLease,
    HERO_IMAGE: LeaseToUpload,
    HERO_IMAGE_ALT: 'Lease to upload',
  },
  [STEPS.CREATE_CUSTOM_LEASE]: {
    HEADING: 'Create a custom lease',
    BODY: 'Zillow worked with local law firms to create a lease agreement tailored to your area. We’ll walk you through a series of questions to fill it out and, after reviewing, you can send it to your renter to sign.',
    DB_VALUE: 'createLease',
    LEASE_TYPE: leaseTypes.ZILLOW,
    LEASE_PATH_SUFFIX: leasePathSuffixes.WIZARD,
    CONFIRM_CS_EVENT: DeactivationModalCSEvents.ConfirmCreateCustomLease,
    DECLINE_CS_EVENT: DeactivationModalCSEvents.DeclineCreateCustomLease,
    HERO_IMAGE: LeaseClipboard,
    HERO_IMAGE_ALT: 'Lease on clipboard',
  },
  [STEPS.UPLOAD_UNSIGNED_LEASE]: {
    HEADING: 'Upload your lease for signing',
    BODY: 'We’ll help you complete your lease agreement and share it with your renter to sign. The signed lease will be stored on Zillow for easy access. We’ll also send you a reminder before your lease term ends so you can renew or update it.',
    DB_VALUE: 'uploadUnsignedLease',
    LEASE_TYPE: leaseTypes.SELF_UPLOAD,
    LEASE_PATH_SUFFIX: leasePathSuffixes.SELF_SERVE,
    CONFIRM_CS_EVENT: DeactivationModalCSEvents.ConfirmUploadUnsignedLease,
    DECLINE_CS_EVENT: DeactivationModalCSEvents.DeclineUploadUnsignedLease,
    HERO_IMAGE: LeaseToUpload,
    HERO_IMAGE_ALT: 'Lease to upload',
  },
};

const LEASES_SURVEY_CONTENT = {
  [STEPS.LEASES_SURVEY]: {
    HEADING: 'Have you signed a lease with the new renter yet?',
    DB_VALUE: 'haveSignedLease',
    SURVEY_OPTIONS: [
      { label: 'Yes, we’ve already signed a lease agreement.', value: true },
      { label: 'No, we still need to sign a lease agreement.', value: false },
    ],
    HERO_IMAGE: LeaseToSign,
    HERO_IMAGE_ALT: 'Lease to sign',
  },
  [STEPS.LEASES_SURVEY_NEEDS_LEASE]: {
    HEADING: 'Let’s get your next lease signed',
    BODY: 'Which best describes your situation?',
    DB_VALUE: 'haveLeaseAgreement',
    SURVEY_OPTIONS: [
      { label: 'I need to create a custom lease agreement.', value: false },
      { label: 'I already have a lease agreement that I need my renter to sign.', value: true },
    ],
  },
};

const REASONS = {
  FOUND_RENTER_ON_ZILLOW: 'FOUND_RENTER_ON_ZILLOW',
  FOUND_RENTER_ELSEWHERE: 'FOUND_RENTER_ELSEWHERE',
  TENANT_RENEWED: 'TENANT_RENEWED',
  STILL_LOOKING_PAUSING_ON_ZILLOW: 'STILL_LOOKING_PAUSING_ON_ZILLOW',
  OTHER: 'OTHER',
  ENOUGH_LEADS: 'ENOUGH_LEADS',
  STOP_ADVERTISING_ON_ZILLOW: 'STOP_ADVERTISING_ON_ZILLOW',
};

const LAUNCHED_FROM_OPTIONS = {
  SOH: 'soh',
  EDIT_LISTING_PAGE: 'editListingPage',
  LISTING_AVAILABILITY_PAGE: 'listingAvailabilityPage',
};

const deactivationReasons = [
  {
    label: 'I found a new tenant through Zillow',
    name: REASONS.FOUND_RENTER_ON_ZILLOW,
  },
  {
    label: 'I found a new tenant elsewhere',
    name: REASONS.FOUND_RENTER_ELSEWHERE,
  },
  {
    label: 'My current tenant renewed',
    name: REASONS.TENANT_RENEWED,
  },
  {
    label: 'I’m still looking for a tenant, but pausing this Zillow listing',
    name: REASONS.STILL_LOOKING_PAUSING_ON_ZILLOW,
  },
  {
    label: 'Other',
    name: REASONS.OTHER,
  },
];

export {
  deactivationReasons,
  CREATE_LEASE_CONTENT,
  LEASE_CREATION_CTA_STEPS,
  LEASES_SURVEY_CONTENT,
  LEASES_SURVEY_STEPS,
  LAUNCHED_FROM_OPTIONS,
  REASONS,
  STEPS,
  submitButtonText,
};
